import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Package, FileDown, ChevronRight, RotateCcw, Wallet, QrCode } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

import { paymentStatusClass } from "@/lib/statusBadge";

const STATUS_LABELS = {
  pending: "Pending", confirmed: "Confirmed", packed: "Packed",
  out_for_delivery: "Out for delivery", delivered: "Delivered", cancelled: "Cancelled",
};

export default function MyOrders() {
  const { brand } = useOutletContext();
  const { brand: key } = useParams();
  const dark = brand.key === "blackpekoe";
  const fg = dark ? "#F5F2EB" : "#2D2622";
  const muted = dark ? "rgba(245,242,235,0.7)" : "#5C5753";
  const subtle = dark ? "rgba(245,242,235,0.55)" : "#8C8581";
  const cardBg = dark ? "rgba(255,255,255,0.04)" : "white";
  const cardBorder = dark ? "rgba(255,255,255,0.08)" : "rgba(45,38,34,0.1)";

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reordering, setReordering] = useState(null);
  const [wallet, setWallet] = useState({ balance: 0, transactions: [] });
  const { merge } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/orders/mine").then(({ data }) => setOrders(data.filter((o) => !o.brand || o.brand === brand.key))).finally(() => setLoading(false));
    api.get("/wallet").then(({ data }) => setWallet(data)).catch(() => {});
  }, [brand.key]);

  const downloadInvoice = async (oid, order_no) => {
    const token = localStorage.getItem("bbf_token");
    const res = await fetch(`${API}/orders/${oid}/invoice`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `invoice-${order_no}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const reorder = async (oid, order_no) => {
    setReordering(oid);
    try {
      const { data } = await api.post(`/orders/${oid}/reorder`);
      if (data.items.length === 0) { toast.error("None of these items are currently available."); return; }
      merge(data.items);
      if (data.skipped.length) toast.warning(`Added ${data.items.length} items. Skipped ${data.skipped.length}: ${data.skipped.map(s => s.name).join(", ")}`);
      else toast.success(`${order_no} re-added to cart`);
      nav(`/b/${key}/cart`);
    } catch (e) { toast.error("Reorder failed"); }
    finally { setReordering(null); }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-8 py-10" style={{ color: fg }}>
      <p className="overline" style={{ color: subtle }}>{brand.name} • My orders</p>
      <div className="flex items-center justify-between mt-2 mb-4 flex-wrap gap-3">
        <h1 className="font-heading font-bold text-3xl">Order history</h1>
        <Link to={`/b/${brand.key}/scan`} data-testid="partner-scan-link"
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold text-white"
          style={{ background: brand.primary }}>
          <QrCode className="w-4 h-4" /> Scan to confirm delivery
        </Link>
      </div>

      <div className="mb-6 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: brand.primary }}><Wallet className="w-5 h-5 text-white" /></div>
          <div>
            <p className="overline" style={{ color: subtle }}>Wallet credit</p>
            <p className="font-heading font-bold text-2xl" data-testid="my-wallet-balance" style={{ color: fg }}>₹{Number(wallet.balance || 0).toFixed(2)}</p>
          </div>
        </div>
        <p className="text-xs max-w-md" style={{ color: muted }}>
          Damaged-product refunds & promotional credits issued by the admin appear here. Apply on any future order at checkout.
        </p>
      </div>

      {wallet.transactions?.length > 0 && (
        <details className="mb-6 rounded-xl p-4" style={{ background: cardBg, border: `1px solid ${cardBorder}` }} data-testid="wallet-history">
          <summary className="cursor-pointer font-semibold text-sm" style={{ color: fg }}>View wallet history ({wallet.transactions.length})</summary>
          <div className="mt-3 space-y-1 max-h-64 overflow-auto text-sm">
            {wallet.transactions.map((t) => (
              <div key={t.id} className="flex justify-between gap-3 py-2" style={{ borderBottom: `1px solid ${cardBorder}` }}>
                <div className="min-w-0">
                  <p className="text-xs" style={{ color: subtle }}>{new Date(t.created_at).toLocaleString()}</p>
                  <p className="truncate">{t.reason}{t.ref_order_no && <span style={{ color: subtle }}> · {t.ref_order_no}</span>}</p>
                </div>
                <span className="font-semibold whitespace-nowrap" style={{ color: t.type === "credit" ? "#3D6B42" : "#A6291E" }}>
                  {t.type === "credit" ? "+" : "−"}₹{Number(t.amount).toFixed(2)}
                </span>
              </div>
            ))}
          </div>
        </details>
      )}

      {loading ? (
        <p style={{ color: subtle }}>Loading…</p>
      ) : orders.length === 0 ? (
        <div className="rounded-xl p-12 text-center" data-testid="no-orders" style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
          <Package className="w-10 h-10 mx-auto" style={{ color: brand.primary }} />
          <p className="mt-3" style={{ color: muted }}>You haven&apos;t placed any orders yet.</p>
          <Link to={`/b/${key}/shop`}><Button className="mt-4 text-white" style={{ background: brand.primary }}>Start shopping</Button></Link>
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <div key={o.id} data-testid={`order-row-${o.id}`}
              className="rounded-xl p-5 flex flex-col sm:flex-row sm:items-center gap-4"
              style={{ background: cardBg, border: `1px solid ${cardBorder}` }}>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <p className="font-heading font-semibold">{o.order_no}</p>
                  <span className={`status-badge status-${o.status}`}>{STATUS_LABELS[o.status]}</span>
                  <span className={`status-badge ${paymentStatusClass(o.payment_status)}`}>{o.payment_status}</span>
                </div>
                <p className="text-sm mt-1" style={{ color: subtle }}>{new Date(o.created_at).toLocaleString()} • {o.items.length} items</p>
                {o.razorpay_payment_id && (
                  <p className="text-[11px] font-mono mt-1" style={{ color: subtle }} title="Razorpay transaction ID" data-testid={`order-txn-${o.id}`}>
                    Txn: {o.razorpay_payment_id}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                <p className="font-heading font-bold text-lg">₹{o.total.toFixed(2)}</p>
                <Button data-testid={`reorder-${o.id}`} variant="outline" size="sm" disabled={reordering === o.id} onClick={() => reorder(o.id, o.order_no)}>
                  <RotateCcw className="w-4 h-4 mr-1" /> {reordering === o.id ? "…" : "Reorder"}
                </Button>
                <Button data-testid={`download-invoice-${o.id}`} variant="outline" size="sm" onClick={() => downloadInvoice(o.id, o.order_no)}>
                  <FileDown className="w-4 h-4 mr-1" /> Invoice
                </Button>
                <Link to={`/b/${key}/orders/${o.id}`}>
                  <Button data-testid={`view-order-${o.id}`} size="sm" className="text-white" style={{ background: brand.secondary }}>
                    View <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
